/**
 * Fast Mystery Bundle Selector
 * Uses server-side pre-processed data (no cart fetch needed)
 * Handles multi-quantity variant selection based on inventory levels
 */

class MysteryBundleSelector {
  constructor() {
    this.init();
  }

  init() {
    // Initialize mystery bundle interfaces on page load
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeMysteryBundles();
    });
  }

  initializeMysteryBundles() {
    const mysteryBundleInterfaces = document.querySelectorAll('[data-mystery-bundle="true"]');
    
    mysteryBundleInterfaces.forEach(element => {
      const productId = element.dataset.productId;
      const sectionId = element.closest('[data-section]')?.dataset.section;
      
      if (productId && sectionId) {
        this.setupMysteryBundle(element, productId, sectionId);
      }
    });
  }

  setupMysteryBundle(element, productId, sectionId) {
    // Get pre-processed variant data from the JSON script tag (no fetch needed)
    const dataScript = document.getElementById(`mystery-bundle-data-${sectionId}`);
    if (!dataScript) {
      console.error('Mystery bundle data not found for section:', sectionId);
      return;
    }

    try {
      this.mysteryBundleData = JSON.parse(dataScript.textContent);
      console.log('✅ Mystery bundle data loaded instantly:', this.mysteryBundleData);
    } catch (error) {
      console.error('Error parsing mystery bundle data:', error);
      return;
    }
  }

  /**
   * Fast mystery bundle cart addition (no cart fetch needed)
   * @param {number} productId - The product ID
   * @param {number} requestedQuantity - How many items user wants
   * @param {Object} cart - Cart drawer element for sections
   * @returns {Promise<Object>} Cart response with sections for drawer
   */
  async handleMysteryBundleAdd(productId, requestedQuantity, cart = null) {
    const startTime = performance.now();
    try {
      console.log('🎁 Fast Mystery Bundle - Start:', { productId, requestedQuantity });
      
      // Step 1: Get mystery bundle data for this product
      const processStart = performance.now();
      const mysteryBundleData = this.getMysteryBundleData(productId);
      const availableVariants = this.getSortedAvailableVariants(mysteryBundleData);
      console.log('✅ Data processing took:', Math.round(performance.now() - processStart), 'ms');
      console.log('✅ Available variants:', availableVariants.map(v => ({ id: v.id, title: v.title, inventory: v.inventory })));
      
      // Step 2: Handle multi-quantity selection with overflow logic
      const selectStart = performance.now();
      const selectedVariants = this.selectVariantsWithOverflow(availableVariants, requestedQuantity, mysteryBundleData);
      console.log('🎯 Selection took:', Math.round(performance.now() - selectStart), 'ms');
      console.log('🎯 Selected variants:', selectedVariants);
      
      // Step 3: Add to cart
      const addStart = performance.now();
      const cartResponse = await this.addVariantsToCart(selectedVariants, cart);
      console.log('🛒 Cart add took:', Math.round(performance.now() - addStart), 'ms');
      
      console.log('⚡ TOTAL FAST MYSTERY BUNDLE TIME:', Math.round(performance.now() - startTime), 'ms');
      
      // Update cart state dynamically for next selection (premium seamless experience)
      this.updateMysteryBundleCartState(selectedVariants, mysteryBundleData);
      
      return { selectedVariants, cartResponse };
      
    } catch (error) {
      console.error('❌ Mystery bundle error after:', Math.round(performance.now() - startTime), 'ms');
      console.error('❌ Error details:', error);
      throw error;
    }
  }

  /**
   * Get mystery bundle data for a specific product
   * @param {number} productId - Product ID to get data for
   * @returns {Object} Mystery bundle data
   */
  getMysteryBundleData(productId) {
    // Find the mystery bundle interface for this product
    const mysteryBundleElement = document.querySelector(`[data-mystery-bundle="true"][data-product-id="${productId}"]`);
    if (!mysteryBundleElement) {
      throw new Error(`Mystery bundle element not found for product ${productId}`);
    }

    // Get the section ID
    const section = mysteryBundleElement.closest('[data-section]');
    if (!section) {
      throw new Error('Section not found for mystery bundle');
    }
    
    const sectionId = section.dataset.section;
    const dataScript = document.getElementById(`mystery-bundle-data-${sectionId}`);
    if (!dataScript) {
      throw new Error(`Mystery bundle data script not found for section ${sectionId}`);
    }

    try {
      const data = JSON.parse(dataScript.textContent);
      console.log('📦 Mystery bundle data retrieved:', data);
      return data;
    } catch (error) {
      throw new Error(`Failed to parse mystery bundle data: ${error.message}`);
    }
  }

  /**
   * Get sorted available variants from data (filter cart items and sort by inventory)
   * @param {Object} mysteryBundleData - Mystery bundle data object
   * @returns {Array<Object>} Available variants sorted by inventory (highest first)
   */
  getSortedAvailableVariants(mysteryBundleData) {
    if (!mysteryBundleData || !mysteryBundleData.allVariants) {
      throw new Error('Mystery bundle data not available');
    }

    const existingCartVariantIds = mysteryBundleData.existingCartVariantIds || [];
    
    // Filter available variants (exclude cart items) and sort by inventory
    return mysteryBundleData.allVariants
      .filter(variant => {
        // Only include available variants not already in cart
        return variant.available && !existingCartVariantIds.includes(variant.id);
      })
      .map(variant => ({
        id: variant.id,
        title: variant.title,
        price: variant.price,
        inventory: variant.inventory_quantity || 999
      }))
      .sort((a, b) => {
        // Sort by inventory (highest first)
        return b.inventory - a.inventory;
      });
  }

  /**
   * Select variants with multi-quantity overflow handling
   * For qty=2: gets highest + second-highest inventory variants
   * @param {Array<Object>} availableVariants - Available variants sorted by inventory (highest first)
   * @param {number} requestedQuantity - Requested quantity
   * @param {Object} mysteryBundleData - Mystery bundle data object
   * @returns {Array<Object>} Selected variants with quantities
   */
  selectVariantsWithOverflow(availableVariants, requestedQuantity, mysteryBundleData) {
    const selections = [];
    let remainingQuantity = requestedQuantity;

    if (availableVariants.length === 0) {
      // If no variants available (all in cart), cycle through all variants anyway
      console.log('⚠️ All variants in cart, cycling through all variants');
      const allVariants = mysteryBundleData.allVariants.filter(v => v.available);
      if (allVariants.length === 0) {
        throw new Error('No available variants to select');
      }
      return this.selectFromAllVariants(allVariants, requestedQuantity);
    }

    // First pass: Add one of each available variant (in inventory order)
    // qty=1: gets highest inventory
    // qty=2: gets highest + second-highest inventory
    // qty=3: gets highest + second + third, etc.
    for (let i = 0; i < availableVariants.length && remainingQuantity > 0; i++) {
      const variant = availableVariants[i];
      selections.push({
        id: variant.id,
        quantity: 1,
        title: variant.title,
        price: variant.price,
        inventory: variant.inventory
      });
      remainingQuantity--;
    }

    // Second pass: For overflow quantity, cycle back through variants (highest inventory first)
    let variantIndex = 0;
    while (remainingQuantity > 0 && availableVariants.length > 0) {
      const variant = availableVariants[variantIndex];
      
      // Find existing selection for this variant and increment quantity
      const existingSelection = selections.find(s => s.id === variant.id);
      if (existingSelection) {
        existingSelection.quantity++;
      } else {
        // This shouldn't happen in second pass, but just in case
        selections.push({
          id: variant.id,
          quantity: 1,
          title: variant.title,
          price: variant.price,
          inventory: variant.inventory
        });
      }
      
      remainingQuantity--;
      variantIndex = (variantIndex + 1) % availableVariants.length;
    }

    return selections;
  }

  /**
   * Select from all variants when none are available (fallback - all already in cart)
   */
  selectFromAllVariants(allVariants, requestedQuantity) {
    const selections = [];
    let remainingQuantity = requestedQuantity;
    
    // Sort by inventory (highest first)
    const sortedVariants = allVariants.sort((a, b) => {
      const aInventory = a.inventory_quantity !== null ? a.inventory_quantity : 999;
      const bInventory = b.inventory_quantity !== null ? b.inventory_quantity : 999;
      return bInventory - aInventory;
    });
    
    // Distribute quantity across variants (cycle through all variants)
    let variantIndex = 0;
    while (remainingQuantity > 0 && sortedVariants.length > 0) {
      const variant = sortedVariants[variantIndex];
      
      const existingSelection = selections.find(s => s.id === variant.id);
      if (existingSelection) {
        existingSelection.quantity++;
      } else {
        selections.push({
          id: variant.id,
          quantity: 1,
          title: variant.title,
          price: variant.price,
          inventory: variant.inventory_quantity || 999
        });
      }
      
      remainingQuantity--;
      variantIndex = (variantIndex + 1) % sortedVariants.length;
    }
    
    return selections;
  }

  /**
   * Add variants to cart in single request with sections for cart drawer
   * @param {Array<Object>} selectedVariants - Variants to add with quantities
   * @param {Object} cart - Cart drawer element for sections
   */
  async addVariantsToCart(selectedVariants, cart = null) {
    // Add all variants in a single request for speed
    const items = selectedVariants.map(selection => ({
      id: selection.id,
      quantity: selection.quantity,
      properties: {
        '_mystery_bundle': 'true'
      }
    }));

    // Send as JSON with sections for cart drawer update
    const requestBody = { items };
    
    // Add sections for cart drawer update (same as normal product form)  
    if (cart) {
      requestBody.sections = cart.getSectionsToRender().map((section) => section.id);
      requestBody.sections_url = window.location.pathname;
    }

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(requestBody)
    };
    
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, config);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to add mystery bundle: ${errorData.description || 'Unknown error'}`);
    }
    
    return await response.json();
  }

  /**
   * Update mystery bundle cart state dynamically after successful addition
   * Ensures next selection gets fresh variants without page refresh
   * @param {Array<Object>} selectedVariants - Variants that were just added
   * @param {Object} mysteryBundleData - Current mystery bundle data to update
   */
  updateMysteryBundleCartState(selectedVariants, mysteryBundleData) {
    console.log('🔄 Updating cart state for premium seamless experience...');
    
    // Add newly selected variant IDs to existing cart variant list
    selectedVariants.forEach(variant => {
      if (!mysteryBundleData.existingCartVariantIds.includes(variant.id)) {
        mysteryBundleData.existingCartVariantIds.push(variant.id);
        console.log(`✅ Added variant ${variant.id} to cart state`);
      }
    });
    
    // Update the JSON script tag for future mystery bundle selections
    try {
      const mysteryBundleElement = document.querySelector(`[data-mystery-bundle="true"][data-product-id="${mysteryBundleData.productId}"]`);
      if (mysteryBundleElement) {
        const section = mysteryBundleElement.closest('[data-section]');
        if (section) {
          const sectionId = section.dataset.section;
          const dataScript = document.getElementById(`mystery-bundle-data-${sectionId}`);
          if (dataScript) {
            dataScript.textContent = JSON.stringify(mysteryBundleData);
            console.log('🎯 Mystery bundle data updated successfully');
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not update mystery bundle data script:', error);
    }
    
    console.log('💎 Cart state updated - next selection will use different variants');
  }

  /**
   * Generate user-friendly success message
   * @param {Array<Object>} selectedVariants - What was added to cart
   * @returns {string} Success message
   */
  generateSuccessMessage(selectedVariants) {
    const totalItems = selectedVariants.reduce((sum, variant) => sum + variant.quantity, 0);
    const uniqueVariants = selectedVariants.length;
    
    if (uniqueVariants === 1) {
      return `Added ${totalItems} mystery bundle item${totalItems > 1 ? 's' : ''} to cart`;
    } else {
      return `Added ${totalItems} mystery bundle items (${uniqueVariants} different variants) to cart`;
    }
  }

  /**
   * Show success feedback to user (silent for mystery bundles)
   * @param {Array<Object>} selectedVariants - What was added
   * @param {HTMLElement} element - Mystery bundle interface element
   */
  showSuccessMessage(selectedVariants, element) {
    // Silent success - no UI feedback needed for mystery bundles
    console.log('Mystery bundle added successfully:', this.generateSuccessMessage(selectedVariants));
  }

  /**
   * Show error message to user (console only for mystery bundles)
   * @param {string} errorMessage - Error to display
   * @param {HTMLElement} element - Mystery bundle interface element
   */
  showErrorMessage(errorMessage, element) {
    // Log error but don't show UI - let normal form error handling take over
    console.error('Mystery bundle error:', errorMessage);
  }
}

// Initialize the mystery bundle selector
if (!window.mysteryBundleSelector) {
  window.mysteryBundleSelector = new MysteryBundleSelector();
}